import { storage } from './config';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

export interface UploadResult {
  url: string;
  path: string;
}

export interface UploadProgress {
  progress: number;
  bytesTransferred: number;
  totalBytes: number;
}

/**
 * Upload a file to Firebase Storage
 * @param file - The file to upload
 * @param path - The storage path (e.g., 'cars/userId/carId/image.jpg')
 * @param onProgress - Optional progress callback
 * @returns Promise with download URL and storage path
 */
export const uploadFile = async (
  file: File,
  path: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<UploadResult> => {
  try {
    const storageRef = ref(storage, path);
    
    // Upload file
    const uploadTask = uploadBytes(storageRef, file);
    
    // Note: uploadBytes doesn't provide progress, but we can simulate it
    if (onProgress) {
      onProgress({
        progress: 0,
        bytesTransferred: 0,
        totalBytes: file.size
      });
      
      // Simulate progress (since uploadBytes doesn't provide real progress)
      const progressInterval = setInterval(() => {
        onProgress({
          progress: 50,
          bytesTransferred: file.size / 2,
          totalBytes: file.size
        });
      }, 100);
      
      await uploadTask;
      clearInterval(progressInterval);
      
      onProgress({
        progress: 100,
        bytesTransferred: file.size,
        totalBytes: file.size
      });
    } else {
      await uploadTask;
    }
    
    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);
    
    return {
      url: downloadURL,
      path: path
    };
  } catch (error) {
    console.error('Error uploading file:', error);
    throw new Error('Failed to upload file');
  }
};

/**
 * Delete a file from Firebase Storage
 * @param path - The storage path to delete
 */
export const deleteFile = async (path: string): Promise<void> => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw new Error('Failed to delete file');
  }
};

/**
 * Generate a unique filename with timestamp
 * @param originalName - Original filename
 * @returns Unique filename
 */
export const generateUniqueFilename = (originalName: string): string => {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 8);
  const extension = originalName.split('.').pop();
  return `${timestamp}_${randomString}.${extension}`;
};

/**
 * Validate file type and size
 * @param file - File to validate
 * @param maxSizeMB - Maximum size in MB
 * @param allowedTypes - Allowed MIME types
 * @returns Validation result
 */
export const validateFile = (
  file: File,
  maxSizeMB: number = 5,
  allowedTypes: string[] = ['image/jpeg', 'image/png', 'image/webp']
): { isValid: boolean; error?: string } => {
  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: `File type not supported. Allowed types: ${allowedTypes.join(', ')}`
    };
  }
  
  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      isValid: false,
      error: `File too large. Maximum size: ${maxSizeMB}MB`
    };
  }
  
  return { isValid: true };
};
